import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaHeart, FaRegHeart, FaShoppingCart, FaStar,
  FaEye, FaGem
} from 'react-icons/fa';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import QuickViewModal from './QuickViewModal';

const ProductCard = ({ product, index = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist,setIsCartOpen } = useCart();
  const { isDarkMode } = useTheme();

  const handleAddToCart = (e,product) => {
    console.log('Adding to cart:', product);
    e.preventDefault();
    addToCart(product.id, 1);
  };

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  const discountPercentage = product.discount ||
    (product.originalPrice && product.price
      ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
      : 0);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5 }}
      className="product-card h-100"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <div className="card h-100 border-0" style={{
        background: isDarkMode ? 'var(--bg-secondary)' : 'white',
        boxShadow: isHovered
          ? '0 20px 40px rgba(128, 0, 32, 0.25)'
          : '0 5px 20px rgba(128, 0, 32, 0.12)',
        transition: 'all 0.3s ease',
        borderRadius: '20px',
        overflow: 'hidden',
        border: '1px solid rgba(128, 0, 32, 0.05)'
      }}>
        {/* Product Image */}
               <Link
  to={`/product/${product.id}`}
  state={{ product }}   // ← pass the whole object
>
        <div className="product-image position-relative" style={{ height: '300px', overflow: 'hidden' }}>
          <LazyLoadImage
            src={product.image}
            alt={product.name}
            effect="blur"
            className="w-100 h-100 object-fit-cover"
            style={{
              transform: isHovered ? 'scale(1.1)' : 'scale(1)',
              transition: 'transform 0.5s ease'
            }}
          />

          {/* Image Overlay on Hover */}
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                background: 'rgba(0, 0, 0, 0.7)',
                backdropFilter: 'blur(5px)'
              }}>
              <div className="d-flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="btn btn-light rounded-circle"
                  onClick={() => setIsCartOpen(true)}
                  style={{ width: '50px', height: '50px' }}>
                  <FaShoppingCart />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="btn btn-light rounded-circle"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowQuickView(true);
                  }}
                  style={{ width: '50px', height: '50px' }}>
                  <FaEye />
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Badges */}
          <div className="position-absolute top-0 start-0 p-3">
            {product.trending && (
              <span className="badge badge-luxury me-2">Trending</span>
            )}
            {discountPercentage > 0 && (
              <span className="badge badge-discount">{discountPercentage}% OFF</span>
            )}
          </div>

          {/* Wishlist Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="btn position-absolute top-0 end-0 m-3"
            onClick={handleWishlistToggle}
            style={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            {isInWishlist(product.id) ? (
              <FaHeart style={{ color: 'var(--primary-gold)' }} />
            ) : (
              <FaRegHeart style={{ color: 'var(--text-primary)' }} />
            )}
          </motion.button>
        </div>
</Link>
        {/* Product Details */}
 
        <div className="card-body">
          {/* Category & Collection */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-uppercase" style={{
              color: 'var(--primary-gold)',
              letterSpacing: '1px',
              fontWeight: '600'
            }}>
              {product.category}
            </small>
            {product.purity && (
              <small className="d-flex align-items-center" style={{ color: 'var(--text-secondary)' }}>
                <FaGem className="me-1" style={{ fontSize: '12px' }} />
                {product.purity}
              </small>
            )}
          </div>

          {/* Product Name */}
          <h5 className="card-title mb-2" style={{
            fontFamily: 'var(--font-secondary)',
            fontWeight: '600',
            color: 'var(--text-primary)',
            fontSize: '18px'
          }}>
            <Link to={`/product/${product.id}`} className="text-decoration-none" style={{
              color: 'inherit',
              transition: 'color 0.3s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = 'var(--primary-gold)'}
            onMouseLeave={(e) => e.target.style.color = 'inherit'}>
              {product.name}
            </Link>
          </h5>

          {/* Rating */}
          <div className="d-flex align-items-center mb-3">
            <div className="d-flex" style={{ color: 'var(--primary-gold)' }}>
              {[...Array(5)].map((_, i) => (
                <FaStar
                  key={i}
                  style={{
                    fontSize: '14px',
                    opacity: i < Math.floor(product.rating || 4.5) ? 1 : 0.3
                  }}
                />
              ))}
            </div>
            <small className="ms-2" style={{ color: 'var(--text-secondary)' }}>
              ({product.reviews || 0} reviews)
            </small>
          </div>

          {/* Price */}
          <div className="d-flex align-items-end justify-content-between">
            <div>
              <div className="d-flex align-items-center">
                <span className="h5 mb-0 gold-text" style={{ fontWeight: '700' }}>
                  {formatPrice(product.price)}
                </span>
              </div>
              {product.originalPrice && product.originalPrice > product.price && (
                <small className="text-decoration-line-through" style={{ color: 'var(--text-light)' }}>
                  {formatPrice(product.originalPrice)}
                </small>
              )}
            </div>

            {/* Quick Add to Cart */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => handleAddToCart(e, product)}
              className="btn btn-sm"
              style={{
                background: 'var(--gradient-gold)',
                color: '#1a1a1a',
                borderRadius: '20px',
                padding: '8px 16px',
                fontWeight: '600',
                fontSize: '12px'
              }}>
              Add to Cart
            </motion.button>
          </div>

          {/* Additional Info */}
          {product.weight && (
            <div className="mt-2">
              <small className="text-muted">Weight: {product.weight}</small>
            </div>
          )}
        </div>

        {/* Stock Status */}
        <div className="card-footer bg-transparent border-0 pb-3">
          <div className="d-flex justify-content-between align-items-center">
            <small className={`${product.stock ? 'text-success' : 'text-danger'}`}>
              {product.stock ? '✓ In Stock' : '✗ Out of Stock'}
            </small>
            {product.collection && (
              <small className="badge" style={{
                background: 'rgba(212, 175, 55, 0.1)',
                color: 'var(--primary-gold)',
                padding: '5px 10px',
                borderRadius: '15px'
              }}>
                {product.collection}
              </small>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;